import { useMutation } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import React, { useRef, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import {
  FormDateFieldDS,
  FormSelectFieldDS,
  FormTextArea,
  FormTextFieldDS,
} from 'components/reusable/form/FormFields'
import { Button, ButtonLink, Modal, Switch } from 'slab'
import { handleGrapeAPIFieldErrors } from 'utils'
import {
  getAdminSidebarPromoCards,
  postAdminSidebarPromoCards,
  putAdminSidebarPromoCardsId,
} from 'utils/web_api'
import {
  type AdminSidebarPromoCard,
  type AdminSidebarPromoCardTargetAudience,
} from 'utils/web_api.schemas'

const MAX_CTA_TEXT_LENGTH = 17
const MAX_IMAGE_SIZE = 10 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

interface SidebarPromoCardFormProps {
  card?: AdminSidebarPromoCard
  onClose: () => void
  onSuccess: () => void
}

type FormValues = {
  title: string
  content: string
  image_alt: string
  cta_text: string
  cta_href: string
  target_audience: AdminSidebarPromoCardTargetAudience
  active: boolean
  expires_at: string
  image: FileList | null
}

const checkExistingActiveCard = async (
  targetAudience: AdminSidebarPromoCardTargetAudience,
  excludeId?: string
): Promise<AdminSidebarPromoCard | null> => {
  try {
    const data = await getAdminSidebarPromoCards({ target_audience: targetAudience })
    const activeCard = data.data?.find((c) => c.active && c.id !== excludeId)
    return activeCard || null
  } catch {
    return null
  }
}

const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) {
    return 'Image must be JPEG, PNG, or WebP format'
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return 'Image must be less than 10MB'
  }
  return null
}

const formatDateForInput = (dateString?: string | null): string => {
  if (!dateString) return ''
  return new Date(dateString).toISOString().split('T')[0]
}

const buildFormData = (values: FormValues): FormData => {
  const formData = new FormData()
  formData.append('title', values.title)
  formData.append('content', values.content)
  formData.append('image_alt', values.image_alt)
  formData.append('cta_text', values.cta_text)
  formData.append('cta_href', values.cta_href)
  formData.append('target_audience', values.target_audience)
  formData.append('active', values.active.toString())
  if (values.expires_at) {
    formData.append('expires_at', new Date(values.expires_at).toISOString())
  }
  if (values.image && values.image.length > 0) {
    formData.append('image', values.image[0])
  }
  return formData
}

const SidebarPromoCardForm: React.FC<SidebarPromoCardFormProps> = ({ card, onClose, onSuccess }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(card?.image_url || null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [existingActiveCard, setExistingActiveCard] = useState<AdminSidebarPromoCard | null>(null)
  const [isCheckingActive, setIsCheckingActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: card?.title || '',
      content: card?.content || '',
      image_alt: card?.image_alt || '',
      cta_text: card?.cta_text || '',
      cta_href: card?.cta_href || '',
      target_audience: card?.target_audience || 'employer',
      active: card?.active || false,
      expires_at: formatDateForInput(card?.expires_at),
      image: null,
    },
  })

  const watchedValues = watch()
  const hasImage = card?.image_url != null || imagePreview != null || (watchedValues.image?.length ?? 0) > 0

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const formData = buildFormData(values)

      if (card?.id) {
        return await putAdminSidebarPromoCardsId(card.id, { body: formData })
      } else {
        return await postAdminSidebarPromoCards({ body: formData })
      }
    },
    onSuccess: () => {
      toast.success(`Sidebar promo card ${card ? 'updated' : 'created'} successfully`)
      onSuccess()
    },
    onError: (error: GrapeAPIErrorType) => {
      if (error.fieldErrors) {
        handleGrapeAPIFieldErrors(error, { setError } as any)
      } else {
        toast.error(error.message || 'Failed to save card')
      }
    },
  })

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validationError = validateImageFile(file)
    if (validationError) {
      toast.error(validationError)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    setValue('image', e.target.files)
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleActiveToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newActive = e.target.checked
    if (newActive && !watchedValues.active) {
      setIsCheckingActive(true)
      const existing = await checkExistingActiveCard(watchedValues.target_audience, card?.id)
      setIsCheckingActive(false)
      if (existing) {
        setExistingActiveCard(existing)
        setShowConfirmModal(true)
        return
      }
    }
    setValue('active', newActive)
  }

  const handleConfirmActive = () => {
    setValue('active', true)
    setShowConfirmModal(false)
    setExistingActiveCard(null)
  }

  const handleCancelActive = () => {
    setShowConfirmModal(false)
    setExistingActiveCard(null)
  }

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (data.expires_at && new Date(data.expires_at) <= new Date()) {
      setError('expires_at', {
        type: 'manual',
        message: 'Expiration date must be in the future',
      })
      return
    }

    await mutation.mutateAsync(data)
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-4'>
          <Button
            type='button'
            variant='tertiary'
            onClick={onClose}
            aria-label='Go back'
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className='text-2xl font-semibold text-gray-900'>
            {card ? 'Edit' : 'Create'} Sidebar Promo Card
          </h1>
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6 bg-white p-6 rounded-lg shadow'>
          <FormTextFieldDS<FormValues>
            fieldId='title'
            fieldLabel='Title *'
            register={register}
            validationRule={{ required: 'Title is required' }}
            error={errors.title}
          />

          <FormTextArea<FormValues>
            fieldId='content'
            fieldLabel='Content *'
            register={register}
            validationRule={{ required: 'Content is required' }}
            error={errors.content}
            className='min-h-[100px]'
          />

          <div>
            <label htmlFor='image' className='block text-sm font-medium text-gray-700 mb-1'>
              Image
            </label>
            <input
              ref={fileInputRef}
              type='file'
              id='image'
              accept='image/jpeg,image/png,image/webp'
              onChange={handleImageChange}
              className='block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer bg-gray-50 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
            />
            {imagePreview && (
              <div className='mt-2'>
                <img src={imagePreview} alt='Preview' className='max-w-xs rounded-md' />
              </div>
            )}
          </div>

          <FormTextFieldDS<FormValues>
            fieldId='image_alt'
            fieldLabel={hasImage ? 'Image Alt Text *' : 'Image Alt Text'}
            register={register}
            validationRule={{
              validate: (value: string) => {
                if (hasImage && !value) {
                  return 'Image alt text is required when an image is attached'
                }
                return true
              },
            }}
            error={errors.image_alt}
          />

          <FormTextFieldDS<FormValues>
            fieldId='cta_text'
            fieldLabel='CTA Text'
            register={register}
            maxLength={MAX_CTA_TEXT_LENGTH}
            validationRule={{
              validate: (value: string) => {
                const ctaHref = watchedValues.cta_href
                if (ctaHref && !value) {
                  return 'CTA text is required when CTA link URL is set'
                }
                return true
              },
            }}
            error={errors.cta_text}
            description={`Maximum ${MAX_CTA_TEXT_LENGTH} characters`}
          />

          <FormTextFieldDS<FormValues>
            fieldId='cta_href'
            fieldLabel='CTA Link URL'
            register={register}
            validationRule={{
              validate: (value: string) => {
                const ctaText = watchedValues.cta_text
                if (ctaText && !value) {
                  return 'CTA link URL is required when CTA text is set'
                }
                return true
              },
            }}
            error={errors.cta_href}
          />

          <FormSelectFieldDS<FormValues>
            fieldId='target_audience'
            fieldLabel='Target Audience *'
            register={register}
            validationRule={{ required: 'Target audience is required' }}
            error={errors.target_audience}
            fieldOptions={[
              { label: 'Employer', value: 'employer' },
              { label: 'Member', value: 'member' },
              { label: 'Partner', value: 'partner' },
            ]}
          />

          <div className='flex items-center space-x-2'>
            <Switch
              checked={watchedValues.active}
              onChange={handleActiveToggle}
              disabled={isCheckingActive}
            />
            <label htmlFor='active' className='text-sm font-medium text-gray-700'>
              Active
              {isCheckingActive && <span className='ml-2 text-xs text-gray-500'>Checking...</span>}
            </label>
          </div>

          <FormDateFieldDS<FormValues>
            fieldId='expires_at'
            fieldLabel='Expires At (optional)'
            register={register}
            error={errors.expires_at}
            min={new Date().toISOString().split('T')[0]}
          />

          <div className='flex justify-end space-x-4'>
            <Button type='button' variant='secondary' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' variant='primary' disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : card ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>

        <div className='bg-white p-6 rounded-lg shadow'>
          <h2 className='text-lg font-semibold text-gray-900 mb-4'>Preview</h2>
          <div className='sticky top-6'>
            <div className='w-full max-w-[197px] mx-auto lg:mx-0'>
              <div className='mb-2'>
                <div className='relative bg-white border border-gray-200 rounded-lg p-3 shadow-sm'>
                  <div className='flex flex-col gap-2'>
                    <h3 className='heading-sm pr-6'>
                      {watchedValues.title || 'Card Title'}
                    </h3>
                    <p className='body'>
                      {watchedValues.content || 'Card content will appear here...'}
                    </p>
                    {imagePreview && (
                      <div className='mt-1'>
                        <img
                          src={imagePreview}
                          alt={watchedValues.image_alt || 'Card image'}
                          className='w-full rounded-md'
                        />
                      </div>
                    )}
                    {watchedValues.cta_text && watchedValues.cta_href && (
                      <div className='mt-1 flex justify-end'>
                        <ButtonLink
                          href={watchedValues.cta_href}
                          variant='tertiary'
                          size='small'
                          width='auto'
                          rightIcon={ArrowRight}
                        >
                          {watchedValues.cta_text}
                        </ButtonLink>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <Modal.Body>
          <Modal.Title>Confirm Activation</Modal.Title>
          <Modal.Description>
            Another card is already active for {watchedValues.target_audience}. Activating this card will deactivate "{existingActiveCard?.title}". Continue?
          </Modal.Description>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-end gap-3'>
            <Modal.Button variant='secondary' onClick={handleCancelActive}>
              Cancel
            </Modal.Button>
            <Modal.Button variant='primary' onClick={handleConfirmActive}>
              Continue
            </Modal.Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default SidebarPromoCardForm
